import {Alert, FlexColumn, FlexRow, Modal, RadioGroupHandler, SubHeading, Text} from '@forecast-it/design-system';
import React, {useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import ProjectUtil, {ProjectNode} from '../../../forecast-app/shared/util/ProjectUtil';
import Util from '../../../forecast-app/shared/util/util';
import BulkUpdateExpenseItemsMutation from '../../../mutations/bulk_update_expense_items_mutation';
import {ExpenseBoolean, ExpenseRow} from '../ExpenseTypes';

type ChangeExpenseFixedPriceBillingModalProps = {
	expenseItems: ExpenseRow[];
	closeModal: () => void;
};

const ChangeExpenseFixedPriceBillingModal = ({
	expenseItems,
	closeModal,
}: ChangeExpenseFixedPriceBillingModalProps): React.ReactElement => {
	const {formatMessage} = useIntl();
	const unLockedItems = useMemo(() => expenseItems.filter(expenseItem => !expenseItem.locked), [expenseItems]);
	const updateableItems = useMemo(
		() =>
			expenseItems.filter(
				expenseItem => !expenseItem.locked && ProjectUtil.isFixedPriceProject(expenseItem.project as ProjectNode | null)
			),
		[expenseItems]
	);

	const [newPartOfFixedPrice, setNewPartOfFixedPrice] = useState<string>('');

	const count = expenseItems.length;
	const lockedCount = expenseItems.length - unLockedItems.length;
	const showAlert = lockedCount > 0;
	const allowSave = newPartOfFixedPrice && count > lockedCount;

	const saveChanges = () => {
		const onSuccess = () => {
			closeModal();
		};

		Util.CommitMutation(
			BulkUpdateExpenseItemsMutation,
			{
				ids: updateableItems.map(expenseItem => expenseItem.id),
				partOfFixedPrice: newPartOfFixedPrice === ExpenseBoolean.TRUE,
			},
			onSuccess
		);
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="expense_management.fixed_price_billing_details" />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'s'}>
					<FlexRow>
						<SubHeading>
							<FormattedMessage id={'expense_management.billing_details'} />
						</SubHeading>
					</FlexRow>
					<FlexRow>
						<Text color={'medium'}>
							<FormattedMessage id={'expense_management.bulk_change_fixed_price_billing_info'} />
						</Text>
					</FlexRow>
				</FlexColumn>
				<FlexColumn gap={'xl'}>
					<RadioGroupHandler onValueChange={setNewPartOfFixedPrice} value={newPartOfFixedPrice}>
						<FlexRow gap={'s'}>
							<RadioGroupHandler.Item value={ExpenseBoolean.TRUE} />
							<FlexColumn>
								<Text>
									{formatMessage({id: 'expense_management.change_fixed_price_billing_part_of_fixed_price'})}
								</Text>
							</FlexColumn>
						</FlexRow>
						<FlexRow gap={'s'}>
							<RadioGroupHandler.Item value={ExpenseBoolean.FALSE} />
							<Text>
								{formatMessage({id: 'expense_management.change_fixed_price_billing_on_top_of_fixed_price'})}
							</Text>
						</FlexRow>
					</RadioGroupHandler>
					{showAlert && (
						<Alert
							text={formatMessage(
								{id: 'expense_management.change_fixed_price_billing.locked_alert'},
								{count, lockedCount}
							)}
							type={Alert.TYPE.INFORMATION}
						/>
					)}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={saveChanges} disabled={!allowSave} dataCy={'confirm'}>
					<FormattedMessage id={'expense_management.actions.confirm_changes'} />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy={'cancel'}>
					<FormattedMessage id={'common.cancel'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default ChangeExpenseFixedPriceBillingModal;
